<template>
  <div id="app" :class="currentTheme">
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>Loading...</p>
      </div>
    </div>

    <!-- Cookie Settings Panel -->
    <div class="cookie-panel" :class="{ open: isCookiePanelOpen }">
      <div class="cookie-icon" @click="toggleCookiePanel">
        <span>🍪</span>
      </div>
      <div class="cookie-content">
        <h3>Cookie Settings</h3>
        <p>
          We use cookies to enhance your experience and analyze our traffic.
        </p>

        <div class="theme-options">
          <h4>Color Theme</h4>
          <div class="theme-selector">
            <button
              v-for="theme in themes"
              :key="theme.name"
              @click="setTheme(theme.name)"
              :class="['theme-btn', { active: currentTheme === theme.name }]"
              :style="{ backgroundColor: theme.colors.primary }"
            >
              {{ theme.label }}
            </button>
          </div>
        </div>

        <div class="cookie-toggles">
          <div class="cookie-toggle">
            <label>
              <input
                type="checkbox"
                v-model="cookieSettings.necessary"
                disabled
              />
              <span>Necessary Cookies (Required)</span>
            </label>
          </div>
          <div class="cookie-toggle">
            <label>
              <input type="checkbox" v-model="cookieSettings.analytics" />
              <span>Analytics Cookies</span>
            </label>
          </div>
          <div class="cookie-toggle">
            <label>
              <input type="checkbox" v-model="cookieSettings.marketing" />
              <span>Marketing Cookies</span>
            </label>
          </div>
        </div>

        <button class="save-btn" @click="saveCookieSettings">
          Save Settings
        </button>
      </div>
    </div>

    <nav class="main-nav" v-if="true">
      <div class="container">
        <div class="nav-header">
          <router-link to="/" class="logo">HOME</router-link>
          <p class="tagline">"A good place to come and sit"</p>
        </div>
        <button class="mobile-menu-btn" @click="isMenuOpen = !isMenuOpen">
          <span class="hamburger"></span>
        </button>
        <div class="nav-links" :class="{ 'nav-open': isMenuOpen }">
          <router-link to="/">Home</router-link>
          <router-link to="/events">Events</router-link>
          <router-link to="/shop" v-if="false">Shop</router-link>
          <router-link to="/donate">Donate</router-link>
          <router-link to="/contact">Contact</router-link>
          <router-link to="/admin" v-if="isAuthenticated && !isAnonymous"
            >Admin</router-link
          >
          <a
            v-if="isAuthenticated && !isAnonymous"
            @click.prevent="handleLogout"
            href="#"
            class="logout-link"
            >Logout</a
          >
        </div>
      </div>
    </nav>
    <router-view></router-view>
    <Footer />
    <Notification />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, provide } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import Footer from "@/components/Footer.vue";
import Notification from "@/components/Notification.vue";

const store = useStore();
const router = useRouter();
const isMenuOpen = ref(false);
const isLoading = ref(false);

// Make loading state available to child components
provide("isLoading", isLoading);

// Cookie panel state
const isCookiePanelOpen = ref(false);
const cookieSettings = ref({
  necessary: true,
  analytics: false,
  marketing: false,
});

// Theme management
const themes = [
  {
    name: "default",
    label: "Default",
    colors: { primary: "#00b894", secondary: "#e1f5eb" },
  },
  {
    name: "dark",
    label: "Dark",
    colors: { primary: "#2d3436", secondary: "#636e72" },
  },
  {
    name: "ocean",
    label: "Ocean",
    colors: { primary: "#0984e3", secondary: "#74b9ff" },
  },
  {
    name: "sunset",
    label: "Sunset",
    colors: { primary: "#d63031", secondary: "#fab1a0" },
  },
];
const currentTheme = ref("default");

// Toggle cookie panel
const toggleCookiePanel = () => {
  isCookiePanelOpen.value = !isCookiePanelOpen.value;
};

// Set theme
const setTheme = (themeName) => {
  currentTheme.value = themeName;
  localStorage.setItem("selectedTheme", themeName);
};

// Save cookie settings
const saveCookieSettings = () => {
  localStorage.setItem("cookieSettings", JSON.stringify(cookieSettings.value));
  isCookiePanelOpen.value = false;
};

// Replace direct getter access with computed properties
const isAuthenticated = computed(() => store.getters["auth/isAuthenticated"]);
const isAdmin = computed(() => store.getters["auth/isAdmin"]);
const isAnonymous = computed(() => store.getters["auth/isAnonymous"]);

// Add heartbeat to check authentication state
let heartbeatInterval = null;
const startHeartbeat = () => {
  // Clear any existing heartbeat
  if (heartbeatInterval) clearInterval(heartbeatInterval);

  // Set up a new heartbeat interval
  heartbeatInterval = setInterval(() => {
    checkAuthState();
  }, 60000); // Check every minute
};

const checkAuthState = () => {
  // If the app is in a state with no auth, try to restore it
  if (!isAuthenticated.value && !isAnonymous.value) {
    console.log("Detected potential auth state loss, attempting to restore...");

    // Check if there's a current user despite the getters saying otherwise
    // This can happen if Vuex state gets out of sync
    const currentUser = store.getters["auth/currentUser"];

    if (!currentUser) {
      // If truly no user, try anonymous sign-in
      store.dispatch("auth/signInAnonymously").catch((error) => {
        console.error("Heartbeat anonymous auth error:", error);
      });
    } else {
      // If there is a user but getters don't show it, try to refresh the state
      store.dispatch("auth/refreshAuthState").catch((error) => {
        console.error("Heartbeat refresh auth state error:", error);
      });
    }
  }
};

// Handle app visibility changes to detect when user returns after idle
const handleVisibilityChange = () => {
  if (document.visibilityState === "visible") {
    // User has returned to the tab, check auth state
    checkAuthState();
  }
};

// Add route watcher to handle loading state
watch(
  () => router.currentRoute.value,
  (to, from) => {
    // Close mobile menu on navigation
    isMenuOpen.value = false;

    // Check if we're returning from event details (to preserve scroll)
    const isReturningFromDetails =
      sessionStorage.getItem("returningFromEventDetails") === "true";

    // Show loading overlay on route changes
    if (to && from) {
      if (to.path !== from.path) {
        isLoading.value = true;

        // Use a shorter delay when returning to Events from EventDetails
        // to prevent interfering with scroll restoration
        const loadingDelay =
          isReturningFromDetails && to.name === "Events" ? 200 : 500;

        // Hide loading after delay
        setTimeout(() => {
          isLoading.value = false;
        }, loadingDelay);
      }
    } else {
      // Initial load - show loading briefly and then hide it
      isLoading.value = true;
      setTimeout(() => {
        isLoading.value = false;
      }, 500);
    }
  },
  { immediate: true }
);

const handleLogout = async () => {
  try {
    await store.dispatch("auth/logout");
    isMenuOpen.value = false;
    if (router.currentRoute.value.meta.requiresAuth) {
      router.push("/login");
    }
  } catch (error) {
    console.error("Logout error:", error);
  }
};

const handleClickOutside = (event) => {
  const nav = document.querySelector(".main-nav");
  const isClickInside = nav.contains(event.target);
  if (!isClickInside && isMenuOpen.value) {
    isMenuOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  document.addEventListener("visibilitychange", handleVisibilityChange);

  // Start the heartbeat
  startHeartbeat();

  // Load saved theme
  const savedTheme = localStorage.getItem("selectedTheme");
  if (savedTheme) {
    currentTheme.value = savedTheme;
  }

  // Load saved cookie settings
  const savedCookieSettings = localStorage.getItem("cookieSettings");
  if (savedCookieSettings) {
    cookieSettings.value = JSON.parse(savedCookieSettings);
  }

  // Delay anonymous sign-in to ensure authentication state is fully loaded
  setTimeout(() => {
    // Double-check authentication state before anonymous sign-in
    // Only proceed if user is still completely unauthenticated after delay
    if (!isAuthenticated.value && !isAnonymous.value) {
      // Check again to make sure we're not overwriting an existing login
      const currentUser = store.getters["auth/currentUser"];
      if (!currentUser) {
        store.dispatch("auth/signInAnonymously").catch((error) => {
          console.error("Anonymous auth error:", error);
        });
      }
    }
  }, 1000); // Delay for 1 second to ensure auth state is established
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("visibilitychange", handleVisibilityChange);

  // Clear the heartbeat interval
  if (heartbeatInterval) clearInterval(heartbeatInterval);
});
</script>

<style lang="scss">
@import "@/assets/scss/main.scss";

/* Set body background to match app */
body {
  margin: 0;
  padding: 0;
  background-color: rgb(225, 245, 235);
}

#app {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  background-color: rgb(225, 245, 235);
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgb(225, 245, 235);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: $tertiary-color;
  font-size: 1.2rem;

  .spinner {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    border: 4px solid rgba(0, 184, 148, 0.1);
    border-top-color: $tertiary-color;
    animation: spin 1s infinite linear;
    margin-bottom: 1rem;
  }

  p {
    margin: 0;
    color: $tertiary-color;
    font-weight: 600;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.main-nav {
  width: 100%;
  background-color: rgb(235, 250, 242);
  padding: 1rem 0;
  position: relative;
  z-index: 1000;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.08);

  .container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 $spacing-sm;
    margin: 0 auto;
    width: 100%;
    box-sizing: border-box;

    @media (min-width: $breakpoint-md) {
      max-width: 1200px;
      padding: 0 $spacing-md;
    }
  }

  .nav-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .logo {
    color: $tertiary-color;
    font-family: $font-family-headers;
    font-size: 1.2rem;
    text-decoration: none;
    font-weight: bold;
  }

  .tagline {
    color: $tertiary-color;
    font-family: $font-family-headers;
    font-size: 0.9rem;
    margin-top: 0.2rem;
  }

  .mobile-menu-btn {
    display: block;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    z-index: 1001;

    .hamburger {
      display: block;
      width: 24px;
      height: 2px;
      background: $tertiary-color;
      position: relative;
      transition: all 0.3s ease;

      &::before,
      &::after {
        content: "";
        position: absolute;
        width: 24px;
        height: 2px;
        background: $tertiary-color;
        left: 0;
        transition: all 0.3s ease;
      }

      &::before {
        top: -6px;
      }

      &::after {
        bottom: -6px;
      }
    }

    @media (min-width: $breakpoint-md) {
      display: none;
    }
  }

  .nav-links {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: rgb(235, 250, 242);
    padding: 1rem;
    flex-direction: column;
    z-index: 999;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.08);

    &.nav-open {
      display: flex;
      animation: slideDown 0.3s ease;
    }

    a {
      color: $tertiary-color;
      text-decoration: none;
      margin: 0.5rem 0;
      padding: 0.5rem 1rem;
      transition: color 0.3s ease;
      font-size: 1.1rem;

      &:hover {
        color: darken($tertiary-color, 10%);
        background-color: rgba(0, 184, 148, 0.1);
      }

      &.router-link-active {
        color: darken($tertiary-color, 10%);
      }
    }

    .logout-link {
      color: $tertiary-color;
      text-decoration: none;
      margin: 0.5rem 0;
      padding: 0.5rem 1rem;
      cursor: pointer;
      font-size: 1.1rem;

      &:hover {
        color: darken($tertiary-color, 10%);
        background-color: rgba(0, 184, 148, 0.1);
      }
    }

    @media (min-width: $breakpoint-md) {
      display: flex;
      position: static;
      flex-direction: row;
      padding: 0;
      background-color: transparent;
      box-shadow: none;

      a,
      .logout-link {
        margin: 0 0 0 2rem;
        padding: 0;
        font-size: 1rem;

        &:hover {
          background-color: transparent;
        }
      }
    }
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Theme Classes */
#app.default {
  --primary-color: #00b894;
  --secondary-color: #e1f5eb;
  --background-color: rgb(225, 245, 235);
  --nav-background: rgb(235, 250, 242);
  --text-color: #2d3436;
}

#app.dark {
  --primary-color: #2d3436;
  --secondary-color: #636e72;
  --background-color: #2d3436;
  --nav-background: #444;
  --text-color: #f5f5f5;

  background-color: var(--background-color);
  color: var(--text-color);

  .main-nav {
    background-color: var(--nav-background);

    .logo,
    .tagline,
    .nav-links a,
    .logout-link {
      color: #f5f5f5;
    }

    .hamburger,
    .hamburger::before,
    .hamburger::after {
      background: #f5f5f5;
    }
  }

  .loading-overlay {
    background: var(--background-color);
  }
}

#app.ocean {
  --primary-color: #0984e3;
  --secondary-color: #74b9ff;
  --background-color: #e3f2fd;
  --nav-background: #bbdefb;
  --text-color: #2c3e50;

  background-color: var(--background-color);

  .main-nav {
    background-color: var(--nav-background);
  }

  .loading-overlay {
    background: var(--background-color);
  }
}

#app.sunset {
  --primary-color: #d63031;
  --secondary-color: #fab1a0;
  --background-color: #fff5f5;
  --nav-background: #fde2e2;
  --text-color: #2d3436;

  background-color: var(--background-color);

  .main-nav {
    background-color: var(--nav-background);
  }

  .loading-overlay {
    background: var(--background-color);
  }
}

/* Cookie Panel Styles */
.cookie-panel {
  position: fixed;
  right: -330px;
  top: 50%;
  transform: translateY(-50%);
  width: 330px;
  background-color: white;
  border-radius: 10px 0 0 10px;
  box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
  transition: right 0.3s ease;
  z-index: 9998;
  display: flex;
  overflow: hidden;

  &.open {
    right: 0;
  }

  .cookie-icon {
    width: 50px;
    height: 50px;
    background-color: $tertiary-color;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 10px;
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    border-radius: 10px 0 0 10px;
    box-shadow: -3px 0 10px rgba(0, 0, 0, 0.1);

    span {
      font-size: 24px;
      animation: wiggle 2s infinite;
    }
  }

  .cookie-content {
    flex: 1;
    padding: 20px;
    padding-left: 60px;
    max-height: 80vh;
    overflow-y: auto;

    h3 {
      margin-top: 0;
      color: $tertiary-color;
    }

    p {
      font-size: 0.9rem;
      color: #666;
    }
  }

  .theme-options {
    margin: 15px 0;

    h4 {
      margin-bottom: 10px;
    }

    .theme-selector {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;

      .theme-btn {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        border: 2px solid transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 0.7rem;

        &.active {
          border-color: #333;
          transform: scale(1.1);
        }
      }
    }
  }

  .cookie-toggles {
    margin: 15px 0;

    .cookie-toggle {
      margin: 10px 0;

      label {
        display: flex;
        align-items: center;
        cursor: pointer;

        input {
          margin-right: 10px;
        }
      }
    }
  }

  .save-btn {
    display: block;
    width: 100%;
    padding: 10px;
    background-color: $tertiary-color;
    color: white;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    margin-top: 10px;

    &:hover {
      background-color: darken($tertiary-color, 10%);
    }
  }
}

@keyframes wiggle {
  0%,
  100% {
    transform: rotate(-10deg);
  }
  50% {
    transform: rotate(10deg);
  }
}

#app {
  // ... existing code ...
}

.loading-overlay {
  // ... existing code ...
}

.main-nav {
  // ... existing code ...
}

@keyframes slideDown {
  // ... existing code ...
}
</style>
